import { CSSProperties, PropsWithChildren, useMemo } from 'react';
import styled from 'styled-components';
import { WorkflowVariant } from './types/WorkflowVariant';
import { useWorkflowGroup } from './WorkflowGroup';

const getBorderByVariant = (variant: WorkflowVariant, theme) => {
  const variants: { [key in WorkflowVariant]: string } = {
    Default: theme.vars.newColor.darkGS2,
    Active: theme.vars.newColor.blue1,
    Incomplete: theme.vars.newColor.darkGS2,
    Error: theme.vars.newColor.red2,
  };

  switch (variant) {
    case 'Active':
    case 'Error':
      return `2px solid ${variants[variant]}`;
    default:
      return `1px solid ${variants[variant]}`;
  }
};

const getBorderColourByVariant = (variant: WorkflowVariant, theme) => {
  const variants: { [key in WorkflowVariant]: string } = {
    Default: theme.vars.newColor.darkGS2,
    Active: theme.vars.newColor.blue1,
    Incomplete: theme.vars.newColor.darkGS2,
    Error: theme.vars.newColor.red2,
  };

  return variants[variant];
};

const Container = styled.div<Pick<WorkflowBoxProps, 'variant' | 'isExtraSize'>>`
  padding: ${({ isExtraSize }) => (isExtraSize ? '32px 40px 40px 40px' : '16px')};
  background-color: ${({ theme }) => theme.vars.newColor.white};
  width: ${({ isExtraSize }) => (isExtraSize ? '696px' : '320px')};
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.15);
  position: relative;

  font-size: 14px;
  font-weight: 350px;

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    border: ${({ theme, variant }) => getBorderByVariant(variant, theme)};
    transition: border 0.2s ease-in;
    position: absolute;
    left: 0;
    top: 0;
    pointer-events: none;
  }

  &.workflow-incomplete:before,
  &.workflow-error:before,
  &.workflow-active:before {
    border: 2px solid ${({ variant, theme }) => getBorderColourByVariant(variant, theme)};
    width: 100%;
    height: 100%;
  }
`;

type StylableComponent = { style?: CSSProperties };

export interface WorkflowBoxProps extends StylableComponent {
  /** ID of the workflow. */
  id: string;
  /** The variant style of the workflow. */
  variant?: WorkflowVariant;
  ariaLabel?: string;
  isExtraSize?: boolean;
}

export const WorkflowBox = ({
  children,
  variant,
  style,
  id,
  ariaLabel,
  isExtraSize,
}: PropsWithChildren<WorkflowBoxProps>) => {
  const { activeId } = useWorkflowGroup();
  const isActive = useMemo(() => activeId === id, [activeId, id]);

  /**
   * Gets the variant based on the current active and the passed variant.
   * Custom set variants will always take precedence over assumed variants (Active or Default)
   */
  const currentVariant: WorkflowVariant = useMemo(() => {
    if (variant && variant !== 'Default') {
      return variant;
    }

    if (isActive) {
      return 'Active';
    }

    return 'Default';
  }, [isActive, variant]);

  return (
    <Container
      id={id}
      className={`workflow workflow-${currentVariant.toLocaleLowerCase()}`}
      style={style}
      variant={currentVariant}
      aria-label={ariaLabel}
      isExtraSize={isExtraSize}
    >
      {children}
    </Container>
  );
};

WorkflowBox.defaultProps = {
  variant: 'Default',
};
